import { useState, useEffect, useRef } from 'react';
import GoBackButton from '../../components/GoBackButton';
import { useNavigate } from 'react-router-dom';
import { fetchListNotification, GET_MARK_ALL_AS_READ } from '../../apis/notification.api';
import NotificationItem from '../../components/Header/Notification/NotificationItem';
import SettingNotifyIcon from '../../assets/setting-notify-icon.svg';
import NotificationSettingsModal from '../../components/Header/Notification/notification-modal.component';
import Cookies from 'js-cookie';
import { useAtom } from 'jotai';
import { profileAtom } from '../../components/Settings/atom';
import AvatarDefault from '../../assets/medium_default_optimized.png';
import { Spinner } from '@chakra-ui/react';
import { useClickOutside } from '../../hooks/useOutsideClick';
import SEO from '../../components/SEO';
import { generateSEOMetadata, dynamicPages } from '../../utils/seo';
import { groupNotificationsByTime } from '../../components/Header/Notification/utils/notification-grouping';

const AllNotifications = () => {
  const navigate = useNavigate();
  const [listNotification, setListNotification] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPage: 0,
    unreadCount: 0
  });
  const [loading, setLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [isOpenSetting, setIsOpenSetting] = useState(false);
  const [modal, setModal] = useState('');
  const [active, setActive] = useState(0);
  const [profile] = useAtom(profileAtom);

  const detailRef = useRef(null);

  useClickOutside(detailRef, () => {
    setIsOpenSetting(false);
  });

  const handleLoadMore = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const scrolled = window.scrollY;
    if (scrolled >= scrollable - 300 && !loading && pagination.page < pagination.totalPage) {
      setLoading(true);
      setPagination((prevPagination) => ({
        ...prevPagination,
        page: prevPagination.page + 1
      }));
    }
  };

  const markAllAsRead = async () => {
    const response = await fetch(GET_MARK_ALL_AS_READ, {
      headers: {
        Authorization: `Bearer ${Cookies.get('token')}`,
        'Content-Type': 'application/json'
      }
    });
    const result = await response.json();
    if (result.success) {
      setListNotification((prevList) =>
        prevList.map((item) => ({
          ...item,
          markAsRead: true
        }))
      );
      setPagination({
        ...pagination,
        unreadCount: 0
      });
    }
    setIsOpenSetting(false);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleLoadMore);
    return () => {
      window.removeEventListener('scroll', handleLoadMore);
    };
  });

  useEffect(() => {
    const fetchData = async () => {
      const isTabChange = pagination.page === 1;
      if (isTabChange) {
        setTabLoading(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await fetchListNotification({
          page: pagination.page,
          limit: pagination.limit,
          readStatus: active === 0 ? 'ALL' : 'UNREAD'
        });

        if (response?.data?.length > 0) {
          setListNotification((prevList) =>
            pagination.page === 1 ? response.data : [...prevList, ...response.data]
          );
          setPagination((prevPagination) => ({
            ...prevPagination,
            totalPage: response.totalPage,
            unreadCount: response.unreadCount
          }));
        } else {
          if (pagination.page === 1) {
            setListNotification([]);
          }
        }
      } catch (error) {
        console.error('Error loading more data:', error);
      } finally {
        if (isTabChange) {
          setTabLoading(false);
        } else {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [pagination.page, active]);

  // Generate SEO metadata for notifications page
  const seoMetadata = generateSEOMetadata.dynamicPage(
    dynamicPages.notifications.name,
    dynamicPages.notifications.url,
    dynamicPages.notifications.description
  );

  return (
    <div>
      <SEO {...seoMetadata} />
      <div className="flex flex-start flex-col px-4 md:px-0">
        <GoBackButton
          onClick={() => {
            navigate('/');
            window.scrollTo(0, 0);
          }}
        />
        <div className="px-2 flex flex-col sticky top-[68px] md:top-[80px] z-20 bg-white pt-3 md:pt-10 max-w-[604px] mx-auto w-full pl-10 md:pl-0">
          <div className="flex justify-between w-full items-center mb-2">
            <span className="font-[800] text-xl leading-[150%] md:text-[32px] text-growth-green-900">
              Notifications
            </span>
            <div className="ml-auto flex items-center gap-3 relative" ref={detailRef}>
              <img
                src={SettingNotifyIcon}
                alt="settings"
                onClick={(e) => {
                  e.stopPropagation();
                  if (listNotification.length > 0) {
                    setIsOpenSetting((prev) => !prev);
                  } else {
                    setModal('notification-settings');
                  }
                }}
                className="cursor-pointer hover:opacity-80 transition-all duration-200"
              />
              {isOpenSetting && (
                <div className="absolute right-0 top-8 w-[166px] p-2 rounded-lg shadow-md border border-gray-200 z-50 bg-white flex flex-col gap-1">
                  <div
                    className="py-[6px] px-[14px] hover:bg-[#A6D38D] rounded-md transition-all cursor-pointer"
                    onClick={() => {
                      setModal('notification-settings');
                    }}
                  >
                    <span className="text-sm font-[900]">Settings</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center h-[35px] w-fit bg-[#F0EDEC] rounded-[8px] p-[2px] shadow-[inset_0_2px_2px_rgba(0,0,0,0.15)]">
              {[
                { label: 'All', count: listNotification.length },
                { label: 'Unread', count: pagination.unreadCount }
              ].map((tab, index) => (
                <button
                  key={tab.label}
                  className={`flex items-center gap-2 h-[30px] px-3 rounded-[7px] text-sm font-[800] ${active === index ? 'bg-white shadow-[0_2px_1px_rgba(0,0,0,0.15)] text-growth-green-900' : 'text-[#4B3A32]'} ${tabLoading && active === index ? 'opacity-70' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (tabLoading || active === index) return;
                    setPagination({
                      ...pagination,
                      page: 1
                    });
                    setActive(index);
                  }}
                >
                  <span>{tab.label}</span>
                  <span
                    className={`min-w-[28px] h-[26px] rounded-[6px] flex items-center justify-center text-[10px] ${index === 1 ? 'bg-[#4BA54A] text-white' : 'bg-[#E7E1DE] text-[#4B3A32]'}`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
            <button
              className="h-[30px] px-3 py-[2px] rounded-[7px] bg-white text-sm text-[#6B6B6B] border-[1px] border-[#CCCCCC] flex items-center gap-2 hover:opacity-80"
              onClick={(e) => {
                e.stopPropagation();
                markAllAsRead();
              }}
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M3.5 8.5L6.6 11.5L12.5 4.5"
                  stroke="#6B6B6B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Mark all read</span>
            </button>
          </div>
        </div>
        <div className="w-full max-w-[604px] mx-auto">
          {tabLoading ? (
            <div className="w-full flex justify-center py-12">
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="green.500"
                size="lg"
              />
            </div>
          ) : (
            <div className="flex flex-col gap-4 mt-6">
              {listNotification.length > 0 ? (
                <>
                  {groupNotificationsByTime(listNotification).map((group, groupIndex) => (
                    <div key={groupIndex} className="mb-4">
                      <div className="px-2 py-2 mb-2">
                        <span className="text-sm font-[900] text-gray-500">{group.label}</span>
                      </div>
                      {group.notifications.map((elm, index) => (
                        <NotificationItem
                          key={`${groupIndex}-${index}`}
                          avatar={profile?.imgUrl || AvatarDefault}
                          elm={elm}
                          index={index}
                          setListNotification={setListNotification}
                          setPagination={setPagination}
                        />
                      ))}
                    </div>
                  ))}
                </>
              ) : (
                <div className="w-full text-center py-8 text-gray-500">No notifications found</div>
              )}
              {loading && (
                <div className="w-full flex justify-center py-6">
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="green.500"
                    size="lg"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {modal === 'notification-settings' ? (
        <NotificationSettingsModal
          isOpen
          title="Notifications Settings"
          onClose={() => setModal('')}
          titleBtn="Done"
          maxW="735px"
          maxH="736px"
        />
      ) : null}
    </div>
  );
};

export default AllNotifications;
